"use client";

import { c as _c } from "react/compiler-runtime";
import { useBreakpoint } from "@dc/hooks";
import { Button, EllipsisTooltip, Tooltip } from "@dc/ui/components";
import { usePagination } from "@heroui/react";
import { AltArrowLeftLinear, AltArrowRightLinear } from "solar-icon-set";
import { cn } from "../../lib/utils";
import { useUiLabels } from "../UiLabels/useUiLabels";
export interface SidePanelPaginationProps {
  /** The total number of pages. */
  total?: number;
  /** The current active page (1-based). */
  page?: number;
  /** The number of items per page. */
  perPage?: number;
  /** The total number of items across all pages (used for results counter like "1 - 20 of 150"). */
  totalItems?: number;
  /** Title of the page or section paginating through. */
  pageTitle?: string;
  /** The function to call when the page changes. Receives the new page number (1-based). */
  onChange: (page: number) => void;
  /** The size of the pagination footer. When 'small', buttons are icon-only with light variant. */
  size?: "default" | "small";
}
export const SidePanelPagination = t0 => {
  const $ = _c(82);
  const {
    total: t1,
    page: t2,
    totalItems,
    pageTitle,
    onChange,
    className,
    size: t3
  } = t0;
  const total = t1 === undefined ? 6 : t1;
  const page = t2 === undefined ? 1 : t2;
  const size = t3 === undefined ? "default" : t3;
  const uiLabels = useUiLabels();
  const isDesktop = useBreakpoint("lg");
  const isSmall = size === "small";
  let t4;
  if ($[0] !== page || $[1] !== total) {
    t4 = {
      total,
      page
    };
    $[0] = page;
    $[1] = total;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const {
    activePage,
    total: totalPages,
    onNext,
    onPrevious
  } = usePagination(t4);
  let t5;
  if ($[3] !== activePage || $[4] !== onChange || $[5] !== onPrevious) {
    t5 = () => {
      onPrevious();
      onChange(activePage - 1);
    };
    $[3] = activePage;
    $[4] = onChange;
    $[5] = onPrevious;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const handlePrevious = t5;
  let t6;
  if ($[7] !== activePage || $[8] !== onChange || $[9] !== onNext) {
    t6 = () => {
      onNext();
      onChange(activePage + 1);
    };
    $[7] = activePage;
    $[8] = onChange;
    $[9] = onNext;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const handleNext = t6;
  if (totalPages === 1) {
    let t7;
    if ($[11] !== className) {
      t7 = cn("flex flex-row items-center justify-center text-text-body-neutral-onMatch-muted", className);
      $[11] = className;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    let t8;
    if ($[13] !== pageTitle) {
      t8 = pageTitle && <EllipsisTooltip content={pageTitle} color="foreground" />;
      $[13] = pageTitle;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    let t9;
    if ($[15] !== t7 || $[16] !== t8) {
      t9 = <div className={t7}>{t8}</div>;
      $[15] = t7;
      $[16] = t8;
      $[17] = t9;
    } else {
      t9 = $[17];
    }
    return t9;
  }
  if (totalItems === 0 || totalItems === undefined) {
    let t7;
    if ($[18] !== className) {
      t7 = cn("flex flex-row items-center justify-center text-text-body-neutral-onMatch-muted", className);
      $[18] = className;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    let t8;
    if ($[20] !== uiLabels.sidePanelPagination) {
      t8 = uiLabels.sidePanelPagination.pageWithNumber(1);
      $[20] = uiLabels.sidePanelPagination;
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    let t9;
    if ($[22] !== t8) {
      t9 = <div><p className="text-sm text-text-body-neutral-onMatch-faint">{t8}</p></div>;
      $[22] = t8;
      $[23] = t9;
    } else {
      t9 = $[23];
    }
    let t10;
    if ($[24] !== t7 || $[25] !== t9) {
      t10 = <div className={t7}>{t9}</div>;
      $[24] = t7;
      $[25] = t9;
      $[26] = t10;
    } else {
      t10 = $[26];
    }
    return t10;
  }
  const t7 = isSmall ? "min-w-0 justify-end overflow-hidden" : "w-full justify-between";
  let t8;
  if ($[27] !== t7) {
    t8 = cn("flex flex-row items-center text-text-body-neutral-onMatch-muted", t7);
    $[27] = t7;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  const t9 = !isSmall;
  const t10 = isSmall ? "light" : "solid";
  const t11 = isSmall || !isDesktop;
  let t12;
  if ($[29] !== isDesktop || $[30] !== isSmall || $[31] !== uiLabels.sidePanelPagination.previous) {
    t12 = isSmall || !isDesktop ? <AltArrowLeftLinear className="size-5" /> : uiLabels.sidePanelPagination.previous;
    $[29] = isDesktop;
    $[30] = isSmall;
    $[31] = uiLabels.sidePanelPagination.previous;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== handlePrevious || $[34] !== t10 || $[35] !== t11 || $[36] !== t12 || $[37] !== uiLabels.sidePanelPagination.previousPage) {
    t13 = <Button variant={t10} aria-label={uiLabels.sidePanelPagination.previousPage} onClick={handlePrevious} isIconOnly={t11}>{t12}</Button>;
    $[33] = handlePrevious;
    $[34] = t10;
    $[35] = t11;
    $[36] = t12;
    $[37] = uiLabels.sidePanelPagination.previousPage;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== t13 || $[40] !== t9 || $[41] !== uiLabels.sidePanelPagination.previous) {
    t14 = <Tooltip content={uiLabels.sidePanelPagination.previous} color="foreground" placement="bottom" isDisabled={t9}>{t13}</Tooltip>;
    $[39] = t13;
    $[40] = t9;
    $[41] = uiLabels.sidePanelPagination.previous;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  const t15 = isSmall ? "min-w-0 max-w-full" : "flex-1";
  let t16;
  if ($[43] !== t15) {
    t16 = cn("flex flex-row items-center justify-center overflow-hidden px-1", t15);
    $[43] = t15;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  const t17 = isSmall ? "flex-row gap-2" : "w-full flex-col";
  let t18;
  if ($[45] !== t17) {
    t18 = cn("flex min-w-0 items-center text-sm text-text-body-neutral-onMatch-muted", t17);
    $[45] = t17;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== isSmall || $[48] !== pageTitle) {
    t19 = pageTitle && <EllipsisTooltip content={pageTitle} color="foreground" className={cn("text-sm", isSmall ? "min-w-0 flex-1" : "w-full min-w-0 text-center")} />;
    $[47] = isSmall;
    $[48] = pageTitle;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  const t20 = !isSmall && "text-center";
  let t21;
  if ($[50] !== t20) {
    t21 = cn("flex-shrink-0 text-sm", t20);
    $[50] = t20;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  let t22;
  if ($[52] !== activePage || $[53] !== t21 || $[54] !== totalPages) {
    t22 = <span className={t21}>{activePage}/{totalPages}</span>;
    $[52] = activePage;
    $[53] = t21;
    $[54] = totalPages;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  let t23;
  if ($[56] !== t18 || $[57] !== t19 || $[58] !== t22) {
    t23 = <div className={t18}>{t19}{t22}</div>;
    $[56] = t18;
    $[57] = t19;
    $[58] = t22;
    $[59] = t23;
  } else {
    t23 = $[59];
  }
  let t24;
  if ($[60] !== t16 || $[61] !== t23) {
    t24 = <div className={t16}>{t23}</div>;
    $[60] = t16;
    $[61] = t23;
    $[62] = t24;
  } else {
    t24 = $[62];
  }
  const t25 = !isSmall;
  const t26 = isSmall ? "light" : "solid";
  const t27 = isSmall || !isDesktop;
  let t28;
  if ($[63] !== isDesktop || $[64] !== isSmall || $[65] !== uiLabels.sidePanelPagination.next) {
    t28 = isSmall || !isDesktop ? <AltArrowRightLinear className="size-5" /> : uiLabels.sidePanelPagination.next;
    $[63] = isDesktop;
    $[64] = isSmall;
    $[65] = uiLabels.sidePanelPagination.next;
    $[66] = t28;
  } else {
    t28 = $[66];
  }
  let t29;
  if ($[67] !== handleNext || $[68] !== t26 || $[69] !== t27 || $[70] !== t28 || $[71] !== uiLabels.sidePanelPagination.nextPage) {
    t29 = <Button variant={t26} type="button" aria-label={uiLabels.sidePanelPagination.nextPage} onClick={handleNext} isIconOnly={t27}>{t28}</Button>;
    $[67] = handleNext;
    $[68] = t26;
    $[69] = t27;
    $[70] = t28;
    $[71] = uiLabels.sidePanelPagination.nextPage;
    $[72] = t29;
  } else {
    t29 = $[72];
  }
  let t30;
  if ($[73] !== t25 || $[74] !== t29 || $[75] !== uiLabels.sidePanelPagination.next) {
    t30 = <Tooltip content={uiLabels.sidePanelPagination.next} color="foreground" placement="bottom" isDisabled={t25}>{t29}</Tooltip>;
    $[73] = t25;
    $[74] = t29;
    $[75] = uiLabels.sidePanelPagination.next;
    $[76] = t30;
  } else {
    t30 = $[76];
  }
  let t31;
  if ($[77] !== t14 || $[78] !== t24 || $[79] !== t30 || $[80] !== t8) {
    t31 = <div className={t8}>{t14}{t24}{t30}</div>;
    $[77] = t14;
    $[78] = t24;
    $[79] = t30;
    $[80] = t8;
    $[81] = t31;
  } else {
    t31 = $[81];
  }
  return t31;
};